class ProductBundleSelector extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Initialize dataset properties
    this.addToCartString = this.dataset.addToCartString;
    this.addingString = this.dataset.addingString;
    this.onePackString = this.dataset.onePackString;
    this.manyPacksString = this.dataset.manyPacksString;
    this.packServiceString = this.dataset.packServiceString;
    this.soldOutString = this.dataset.soldOutString;
    this.bundleContainer = this.querySelector('.product-bundle-container');
    this.bundleBoxes = this.querySelectorAll('.bundle_sel_box');
    this.addToCartBtn = this.querySelector('.bundle_atc_btn');

    // Subscription integration
    this.subscriptionSelector = document.querySelector('bundle-subscription-selector');

    this.setupEventListeners();
    this.updateDisplay();

    // Setup dropdown listeners for initially active bundle
    this.setupDropdownListeners();

    // Initial price update for active bundle with pre-selected variants
    this.updateBundlePrice();
  }

  setupEventListeners() {
    // Bundle box selection using event delegation
    this.addEventListener('click', (e) => {
      const bundleBox = e.target.closest('.bundle_sel_box');

      // Don't trigger if clicking on dropdown or its container
      if (e.target.matches('select') || e.target.closest('select') || e.target.closest('.bundle_selbox_variant_selection')) {
        return;
      }

      if (bundleBox) {
        this.selectBundle(bundleBox);
      }
    });

    // Dropdown change handlers using event delegation
    this.addEventListener('change', (e) => {
      if (e.target.matches('.bundle_selbox_frequency')) {
        this.updateBundlePrice();
        this.notifySubscriptionSelector();

        // Dispatch bundle change event for subscription widget
        const activeBox = this.querySelector('.bundle_sel_box.active');
        if (activeBox) {
          document.dispatchEvent(new CustomEvent('bundle-changed', {
            detail: {
              quantity: parseInt(activeBox.dataset.quantity),
              discount: parseInt(activeBox.dataset.discount) || 0
            }
          }));
        }
      }
    });

    // Add to cart handler
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }

    // Listen for subscription mode changes
    document.addEventListener('subscription-mode-changed', (e) => {
      this.handleSubscriptionChange(e.detail);
    });
  }

  selectBundle(selectedBox) {
    // Remove active class from all boxes
    this.bundleBoxes.forEach(box => box.classList.remove('active'));

    // Add active class to selected box
    selectedBox.classList.add('active');

    // Re-setup dropdown event listeners for the newly active box
    this.setupDropdownListeners();

    // Update display
    this.updateDisplay();
    this.updateBundlePrice();

    // Notify subscription widget
    this.notifySubscriptionSelector();

    // Dispatch bundle change event
    document.dispatchEvent(new CustomEvent('bundle-changed', {
      detail: {
        quantity: parseInt(selectedBox.dataset.quantity),
        discount: parseInt(selectedBox.dataset.discount) || 0
      }
    }));
  }

  setupDropdownListeners() {
    // Remove existing listeners and add fresh ones for all visible dropdowns
    const activeDropdowns = this.querySelectorAll('.bundle_sel_box.active .bundle_selbox_frequency');

    activeDropdowns.forEach(dropdown => {
      // Clone the node to remove all existing event listeners
      const newDropdown = dropdown.cloneNode(true);
      dropdown.parentNode.replaceChild(newDropdown, dropdown);

      // Add fresh event listener
      newDropdown.addEventListener('change', (e) => {
        e.stopPropagation();
        this.updateBundlePrice();
      });

      // Ensure the dropdown is focusable and clickable
      newDropdown.style.pointerEvents = 'auto';
      newDropdown.style.position = 'relative';
      newDropdown.style.zIndex = '1';
    });
  }

  #getLineDiscount(selectedOption, quantity, fallbackDiscount) {
    if (!selectedOption) return fallbackDiscount;

    const lineDiscount = parseInt(selectedOption.dataset[`discount${quantity}`], 10);

    if (!Number.isNaN(lineDiscount) && lineDiscount >= 0) {
      return lineDiscount;
    }

    return fallbackDiscount;
  }

  updateBundlePrice() {
    const activeBox = this.querySelector('.bundle_sel_box.active');
    if (!activeBox) return;

    const quantity = parseInt(activeBox.dataset.quantity);
    const fallbackDiscount = parseInt(activeBox.dataset.discount) || 0;

    if (quantity === 1) return;

    const dropdowns = activeBox.querySelectorAll('.bundle_selbox_frequency');
    let totalPrice = 0;
    let totalCompareAtPrice = 0;
    let discountedPrice = 0;
    let hasAnyDiscount = false;

    dropdowns.forEach(dropdown => {
      const selectedOption = dropdown.options[dropdown.selectedIndex];
      const price = parseInt(selectedOption.dataset.price) || 0;
      const lineDiscount = this.#getLineDiscount(selectedOption, quantity, fallbackDiscount);
      const compareAtPriceAttr = selectedOption.dataset.compareAtPrice;
      let compareAtPrice = 0;

      if (compareAtPriceAttr && compareAtPriceAttr.trim() !== '' && compareAtPriceAttr !== '0') {
        compareAtPrice = parseInt(compareAtPriceAttr) || 0;
      }

      if (lineDiscount > 0) {
        hasAnyDiscount = true;
      }

      totalPrice += price;
      totalCompareAtPrice += compareAtPrice;
      discountedPrice += price * (100 - lineDiscount) / 100;
    });

    const averageDiscountedPrice = discountedPrice / quantity;
    const averageCompareAtPrice = totalCompareAtPrice / quantity;
    const priceElement = activeBox.querySelector('.bundle_sel_curr_price');
    const strikethroughElement = activeBox.querySelector('.bundle_sel_price_container s');

    if (priceElement) {
      priceElement.innerHTML = this.formatMoney(averageDiscountedPrice);
    }

    if (strikethroughElement) {
      if (totalCompareAtPrice > 0 && averageCompareAtPrice > averageDiscountedPrice) {
        strikethroughElement.innerHTML = this.formatMoney(averageCompareAtPrice);
        strikethroughElement.style.display = 'inline';
      } else if (hasAnyDiscount || discountedPrice < totalPrice) {
        const averageOriginalPrice = totalPrice / quantity;
        strikethroughElement.innerHTML = this.formatMoney(averageOriginalPrice);
        strikethroughElement.style.display = 'inline';
      } else {
        strikethroughElement.style.display = 'none';
      }
    }

    const totalServings = quantity * 15;
    const pricePerServing = discountedPrice / totalServings;
    const titleElement = activeBox.querySelector('.bundle_curr_title');

    if (titleElement) {
      const packText = quantity === 1 ? this.onePackString : this.manyPacksString;
      titleElement.innerHTML = `${quantity} ${packText} • ${this.formatMoney(pricePerServing)}<span class="bundle_selbox_small_text">/${this.packServiceString}</span>`;
    }
  }

  updateDisplay() {
    const activeBox = this.querySelector('.bundle_sel_box.active');
    if (!activeBox || !this.addToCartBtn) return;

    // Enable add to cart button
    this.addToCartBtn.disabled = false;
    this.addToCartBtn.textContent = this.addToCartString;

    if (this.dataset.soldOut === 'true') {
      this.addToCartBtn.textContent = this.soldOutString;
      this.addToCartBtn.disabled = true;
    }
  }

  async handleAddToCart() {
    const activeBox = this.querySelector('.bundle_sel_box.active');
    if (!activeBox) return;

    // Disable button and show loading
    this.addToCartBtn.disabled = true;
    this.addToCartBtn.textContent = this.addingString;

    const quantity = parseInt(activeBox.dataset.quantity);
    const items = [];

    // Get subscription data
    const subscriptionData = this.getSubscriptionData();

    try {
      if (quantity === 1) {
        // Single pack - use main product
        const productId = this.addToCartBtn.dataset.productId;
        const item = {
          id: productId,
          quantity: 1
        };

        // Add selling plan if subscription is selected
        if (subscriptionData.isSubscription && subscriptionData.sellingPlan) {
          item.selling_plan = subscriptionData.sellingPlan;
        }

        items.push(item);
      } else {
        // Multiple packs - get from dropdowns
        const dropdowns = activeBox.querySelectorAll('.bundle_selbox_frequency');

        dropdowns.forEach(dropdown => {
          const selectedOption = dropdown.options[dropdown.selectedIndex];
          const variantId = selectedOption.value;

          if (variantId && !selectedOption.disabled) {
            const item = {
              id: variantId,
              quantity: 1
            };

            // Add selling plan if subscription is selected
            if (subscriptionData.isSubscription && subscriptionData.sellingPlan) {
              item.selling_plan = subscriptionData.sellingPlan;
            }

            items.push(item);
          }
        });
      }

      await this.addToCart(items);
      this.handleAddToCartSuccess();
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.handleAddToCartError();
    }
  }

  async addToCart(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items: items })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  handleAddToCartSuccess() {
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.textContent = this.addToCartString || 'Add to cart';
    }

    const cartUrl = (window.getCartUrl && window.getCartUrl()) || (window.routes && window.routes.cart_url) || '/cart';
    const log = window.log;
    if (log) log.collapse({ cartUrl }, 'Add to Cart Success | Product Bundle', 'info');

    if (window.onAddToCartSuccess) window.onAddToCartSuccess(cartUrl);
    else window.location.href = cartUrl;
  }

  handleAddToCartError() {
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.textContent = this.addToCartString || 'Add to cart';
    }

    // Show error message
    alert('Error adding to cart. Please try again.');
  }

  formatMoney(cents) {
    const formatted = window.MoneyUtils.formatMoney(cents);
    return `<span class="money">${formatted}</span>`;
  }

  // Subscription integration methods
  handleSubscriptionChange(subscriptionData) {
    // Recalculate bundle pricing when subscription mode changes
    this.updateBundlePrice();
  }

  notifySubscriptionSelector() {
    // Notify subscription selector when bundle selection changes
    if (this.subscriptionSelector) {
      const event = new CustomEvent('bundle-selection-changed', {
        detail: {
          bundleData: this.getActiveBundleData()
        },
        bubbles: true
      });

      this.subscriptionSelector.dispatchEvent(event);
    }
  }

  getSubscriptionData() {
    if (this.subscriptionSelector && typeof this.subscriptionSelector.getSubscriptionData === 'function') {
      return this.subscriptionSelector.getSubscriptionData();
    }

    return {
      isSubscription: false,
      sellingPlan: null,
      discountPercent: 0
    };
  }

  getActiveBundleData() {
    const activeBox = this.querySelector('.bundle_sel_box.active');

    if (!activeBox) return null;

    return {
      quantity: parseInt(activeBox.dataset.quantity),
      discount: parseInt(activeBox.dataset.discount) || 0
    };
  }
}

// Register the custom element
if (!customElements.get('product-bundle-selector')) {
  customElements.define('product-bundle-selector', ProductBundleSelector);
}

// Fallback for browsers that don't support custom elements
document.addEventListener('DOMContentLoaded', function () {
  if (!window.customElements) {
    // Fallback initialization for older browsers
    const bundleSelectors = document.querySelectorAll('product-bundle-selector');
    bundleSelectors.forEach(selector => {
      const instance = new ProductBundleSelector();
      instance.bundleContainer = selector.querySelector('.product-bundle-container');
      instance.bundleBoxes = selector.querySelectorAll('.bundle_sel_box');
      instance.addToCartBtn = selector.querySelector('.bundle_atc_btn');
      instance.setupEventListeners();
      instance.updateDisplay();
    });
  }
});
